import { Suspense } from "react"
import { useTranslation } from "react-i18next"
import { CircleNotch } from "@phosphor-icons/react"
import { ScrollArea } from "@/components/ui/scroll-area"
import { SearchItem } from "@/components/ui/search"
import { AgentOption } from "@/hooks/useAgentOptions"
import { useWorkingUserOptions } from "@/hooks/useWorkingUserOptions"
import { SearchOption } from "@/modules/patients/components/search-option"
import { UserRole } from "@/types/__generated__/union-types"

const MIN_SEARCH_CHARACTERS = 3

type WorkingUserOptionsProps = {
  roles?: ReadonlyArray<UserRole>
  skip: boolean
  searchValue: string
  onSelectOption: (option: AgentOption) => void
}

const WorkingUserOptionsLoading = () => (
  <div className="flex h-77.5 items-center justify-center">
    <CircleNotch className="text-on-surface-subdued animate-spin" size={24} />
  </div>
)

const WorkingUserOptionsList = ({ roles, skip, searchValue, onSelectOption }: WorkingUserOptionsProps) => {
  const { t } = useTranslation()
  const workingUserOptions = useWorkingUserOptions(roles, skip)

  const trimmedSearchValue = searchValue.trim()
  const filteredOptions =
    trimmedSearchValue.length < MIN_SEARCH_CHARACTERS
      ? workingUserOptions
      : workingUserOptions.filter((option) => {
          const searchTerms = trimmedSearchValue.toLowerCase().split(/\s+/)
          const label = option.label.toLowerCase()
          return searchTerms.every((term) => label.includes(term))
        })

  const hasResults = filteredOptions.length > 0

  return (
    <>
      {!hasResults && (
        <SearchItem
          disabled
          className="text-on-surface-subdued z-30 mx-2 cursor-default overflow-hidden rounded-md py-1.5">
          {t("common.nothingFound")}
        </SearchItem>
      )}
      <ScrollArea className="h-77.5">
        <div className="grid max-w-81 gap-0.5 last:mb-2">
          {hasResults &&
            filteredOptions.map((option) => {
              const { value, label, icon, specialties } = option
              return (
                <div key={value}>
                  <SearchOption
                    searchValue={searchValue}
                    value={value}
                    title={label}
                    onSelect={() => onSelectOption(option)}
                    icon={icon}
                    className="mx-2 px-2 py-1"
                    description={specialties}
                  />
                </div>
              )
            })}
        </div>
      </ScrollArea>
    </>
  )
}

export const WorkingUserOptions = (props: WorkingUserOptionsProps) => (
  <Suspense fallback={<WorkingUserOptionsLoading />}>
    <WorkingUserOptionsList {...props} />
  </Suspense>
)
